import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import { useTranslations } from 'next-intl';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';
import { terraProxyApiClient } from '../clients/testerClient';
import {
  APPLICATION_MEMORY_OVERLAYS_QUERY_KEY,
  MEMORY_EXTRACTION_POLL_INTERVAL_MS,
  mergeMemoriesWithOverlays,
  readPersistedApplicationMemoryOverlays,
  resolveMemoryOverlays,
  writePersistedApplicationMemoryOverlays,
  type ApplicationMemoryOverlay,
} from './applicationMemoryOverlays';

type UseMemoriesParams = {
  applicationId: string | undefined;
  searchTerm?: string;
  enabled?: boolean;
};

type UseMemoriesReturn = {
  memories: MemoryRecordResponse[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  invalidate: () => Promise<void>;
};

export function useMemories({
  applicationId,
  searchTerm = '',
  enabled = true,
}: UseMemoriesParams): UseMemoriesReturn {
  const queryClient = useQueryClient();
  const t = useTranslations('memories');
  const categoryMismatchReason = t('rejectionReason.categoryMismatch');

  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);

  const overlaysQueryKey = useMemo(
    () => [APPLICATION_MEMORY_OVERLAYS_QUERY_KEY, applicationId] as const,
    [applicationId],
  );

  const { data: overlays = [] } = useQuery({
    queryKey: overlaysQueryKey,
    queryFn: async (): Promise<ApplicationMemoryOverlay[]> =>
      queryClient.getQueryData<ApplicationMemoryOverlay[]>(overlaysQueryKey) ??
      (applicationId
        ? readPersistedApplicationMemoryOverlays(applicationId)
        : []),
    initialData: () =>
      applicationId
        ? readPersistedApplicationMemoryOverlays(applicationId)
        : [],
    enabled: enabled && Boolean(applicationId),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (!applicationId) {
      return;
    }
    writePersistedApplicationMemoryOverlays({ applicationId, overlays });
  }, [applicationId, overlays]);

  const hasPendingOverlay = overlays.some(
    (overlay) => overlay.status === 'pending',
  );

  const queryResult = useQuery({
    queryKey: ['memories', applicationId],
    queryFn: async () => {
      if (!applicationId) return { records: [], totalCount: 0 };
      return terraProxyApiClient.getMemoriesByApplication(applicationId);
    },
    enabled: enabled && !!applicationId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    refetchInterval: hasPendingOverlay
      ? MEMORY_EXTRACTION_POLL_INTERVAL_MS
      : undefined,
  });

  const bedrockRecords = useMemo(
    () => queryResult.data?.records ?? [],
    [queryResult.data?.records],
  );

  useEffect(() => {
    if (!applicationId) {
      return;
    }

    const applyResolution = () => {
      const currentOverlays =
        queryClient.getQueryData<ApplicationMemoryOverlay[]>(
          overlaysQueryKey,
        ) ?? [];
      if (currentOverlays.length === 0) {
        return;
      }

      const cachedRecords =
        queryClient.getQueryData<{ records: MemoryRecordResponse[] }>([
          'memories',
          applicationId,
        ])?.records ?? bedrockRecords;

      const nextOverlays = resolveMemoryOverlays({
        overlays: currentOverlays,
        bedrockRecords: cachedRecords,
        categoryMismatchReason,
      });

      const unchanged =
        nextOverlays.length === currentOverlays.length &&
        nextOverlays.every((overlay, index) => {
          const current = currentOverlays[index];
          return (
            overlay.recordId === current?.recordId &&
            overlay.status === current?.status &&
            overlay.rejectionReason === current?.rejectionReason &&
            overlay.beforeSnapshot.length === current.beforeSnapshot.length &&
            overlay.beforeSnapshot.every(
              (snapshot, snapshotIndex) =>
                snapshot.recordId ===
                  current.beforeSnapshot[snapshotIndex]?.recordId &&
                snapshot.content ===
                  current.beforeSnapshot[snapshotIndex]?.content &&
                snapshot.createdAt ===
                  current.beforeSnapshot[snapshotIndex]?.createdAt,
            )
          );
        });

      if (!unchanged) {
        queryClient.setQueryData(overlaysQueryKey, nextOverlays);
        writePersistedApplicationMemoryOverlays({
          applicationId,
          overlays: nextOverlays,
        });
      }
    };

    applyResolution();

    if (!hasPendingOverlay) {
      return;
    }

    const intervalId = window.setInterval(
      applyResolution,
      MEMORY_EXTRACTION_POLL_INTERVAL_MS,
    );
    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    applicationId,
    bedrockRecords,
    categoryMismatchReason,
    hasPendingOverlay,
    overlaysQueryKey,
    queryClient,
    queryResult.dataUpdatedAt,
  ]);

  const mergedMemories = mergeMemoriesWithOverlays({
    bedrockRecords,
    overlays,
  });

  const filteredMemories = useMemo(() => {
    if (!applicationId) return mergedMemories;

    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      return mergedMemories.filter((memory) =>
        memory.content.toLowerCase().includes(searchLower),
      );
    }

    return mergedMemories;
  }, [mergedMemories, applicationId, debouncedSearchTerm]);

  const refetch = useCallback(async () => {
    await queryResult.refetch();
  }, [queryResult]);

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['memories'] });
  }, [queryClient]);

  return {
    memories: filteredMemories,
    totalCount: filteredMemories.length,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    refetch,
    invalidate,
  };
}
